import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { api } from '../../services/api';
import EntityPicker, { type PickerOption } from './EntityPicker';
import { mergeCustomers, type PickerCustomer } from './customerPickerModel';

/** The cached customer list is shared by every picker on the page. */
const LIST_LIMIT = 500;
const SHOWN = 50;

export interface CustomerPickerProps {
  /** Selected organization id, or '' for none / all. */
  value: string;
  onChange: (id: string, customer: PickerCustomer | null) => void;
  /** Visible label (also the accessible name). */
  label: string;
  /** Keep the label for screen readers only (toolbars). */
  hideLabel?: boolean;
  /** Shown when nothing is selected, e.g. "כל הלקוחות" in a filter. */
  placeholder?: string;
  /** Offer a clear button when a customer is selected. */
  clearable?: boolean;
  required?: boolean;
  invalid?: boolean;
  /** Ids of a hint / error under the field. */
  describedBy?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Searchable customer (organization) picker (PRD §4.17, T-41): replaces raw
 * organization-id inputs. Since P-41 a thin wrapper on `ui/EntityPicker`: it
 * loads the customers the user may see once (cached) and, when the list is
 * truncated, searches the typed text on the server. Behaviour is unchanged.
 */
export default function CustomerPicker({
  value,
  onChange,
  label,
  hideLabel = false,
  placeholder,
  clearable = true,
  required = false,
  invalid = false,
  describedBy,
  id,
  disabled = false,
  className = '',
}: CustomerPickerProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const inputId = id ?? `${baseId}-input`;
  const labelId = `${baseId}-label`;

  const [query, setQuery] = useState('');
  const [serverQuery, setServerQuery] = useState('');

  const listQuery = useQuery({
    queryKey: ['customer-picker', 'organizations'],
    queryFn: () => api.getOrganizations({ limit: LIST_LIMIT, sort: 'name' }),
    staleTime: 5 * 60_000,
  });
  const base: PickerCustomer[] = useMemo(() => (listQuery.data?.data ?? []) as PickerCustomer[], [listQuery.data]);
  const truncated = base.length >= LIST_LIMIT;

  // Debounced server search, only when the cached list is truncated.
  useEffect(() => {
    if (!truncated) return;
    const q = query.trim();
    const h = window.setTimeout(() => setServerQuery(q.length >= 2 ? q : ''), 250);
    return () => window.clearTimeout(h);
  }, [query, truncated]);
  const searchQuery = useQuery({
    queryKey: ['customer-picker', 'search', serverQuery],
    queryFn: () => api.getOrganizations({ search: serverQuery, limit: SHOWN, sort: 'name' }),
    enabled: truncated && !!serverQuery,
    staleTime: 60_000,
  });

  const all = useMemo(
    () => mergeCustomers(base, (searchQuery.data?.data ?? []) as PickerCustomer[]),
    [base, searchQuery.data],
  );
  const options: PickerOption[] = useMemo(
    () => [...all]
      .sort((a, b) => a.name.localeCompare(b.name, 'he'))
      .map((c) => ({
        value: c.id,
        label: c.name,
        secondary: c.active === false ? t('finance.picker.inactive') : undefined,
        keywords: c.tax_id ?? undefined,
      })),
    [all, t],
  );

  return (
    <div className={className}>
      <label id={labelId} htmlFor={inputId} className={hideLabel ? 'sr-only' : 'label'}>
        {label}
        {required && <span className="text-danger-700" aria-hidden="true"> *</span>}
      </label>
      <EntityPicker
        id={inputId}
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        options={options}
        value={value}
        onChange={(v) => onChange(v, v ? all.find((c) => c.id === v) ?? null : null)}
        icon={Building2}
        placeholder={placeholder ?? t('finance.picker.placeholder')}
        clearable={clearable}
        required={required}
        invalid={invalid}
        disabled={disabled}
        loading={listQuery.isLoading}
        error={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        retrying={listQuery.isFetching}
        onQueryChange={setQuery}
        searching={searchQuery.isFetching}
        limit={SHOWN}
        texts={{
          clear: (name) => t('finance.picker.clear', { name }),
          toggle: t('finance.picker.toggle'),
          loading: t('finance.picker.loading'),
          loadError: t('finance.picker.loadError'),
          noMatches: t('finance.picker.noMatches'),
          searching: t('finance.picker.searching'),
          unknown: t('finance.picker.unknown'),
          more: (count) => t('finance.picker.more', { count }),
        }}
      />
    </div>
  );
}
